import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { WhatsAppTemplate, TemplateComponent } from '../types/template';

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: WhatsAppTemplate | null;
}

export function TemplatePreviewModal({ isOpen, onClose, template }: TemplatePreviewModalProps) {
  const { theme } = useTheme();
  const [currentTime] = useState(new Date().toLocaleTimeString('pt-BR', { 
    hour: '2-digit', 
    minute: '2-digit' 
  }));

  if (!isOpen || !template) return null;

  // Função para renderizar componentes de mídia do header
  const renderHeaderMedia = (component: TemplateComponent) => {
    if (component.format === 'IMAGE') {
      return (
        <div className="relative">
          <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 rounded-t-lg flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Imagem do Header</p>
            </div>
          </div>
        </div>
      );
    } else if (component.format === 'VIDEO') {
      return (
        <div className="relative">
          <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 rounded-t-lg flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Vídeo do Header</p>
            </div>
          </div>
        </div>
      );
    } else if (component.format === 'DOCUMENT') {
      return (
        <div className="relative">
          <div className="w-full h-24 bg-gray-200 dark:bg-gray-700 rounded-t-lg flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Documento do Header</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Função para renderizar botões
  const renderButtons = (component: TemplateComponent) => {
    if (component.type === 'BUTTONS' && component.buttons && component.buttons.length > 0) {
      return (
        <div className="mt-3 space-y-2">
          {component.buttons.map((button, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-2 px-3 border-t border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-center">
                <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-blue-500 text-sm font-medium">{button.text}</span>
              </div>
              {button.type === 'URL' && (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              )}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Função para processar texto com variáveis
  const processTextWithVariables = (text: string) => {
    if (!text) return '';
    
    // Substituir variáveis {{1}}, {{2}}, etc. por exemplos
    return text.replace(/\{\{(\d+)\}\}/g, (match, number) => {
      const examples = [
        'João Silva',
        'Maria Santos', 
        'Pedro Oliveira',
        'Ana Costa',
        'Carlos Mendes'
      ];
      return examples[parseInt(number) - 1] || `[Variável ${number}]`;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header do Modal */}
        <div className={`flex items-center justify-between p-4 border-b sticky top-0 z-10 rounded-t-2xl ${
          theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
        }`}>
          <h3 className="text-xl font-semibold text-n-slate-12">
            Pré-visualização da Template
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'hover:bg-gray-700 text-gray-400'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Conteúdo do Modal */}
        <div className="p-6">
          {/* Informações da Template */}
          <div className={`mb-4 p-3 rounded-lg ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">
                {template.category === 'MARKETING' ? '📢' : 
                 template.category === 'UTILITY' ? '🔧' : '🔐'}
              </span>
              <span className={`font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {template.name}
              </span>
            </div>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {template.category === 'MARKETING' ? 'Marketing' : 
               template.category === 'UTILITY' ? 'Utilidade' : 'Autenticação'} • {template.language}
            </p>
          </div>

          {/* Simulação do WhatsApp */}
          <div className="space-y-4">
            <p className={`text-sm font-medium ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Como aparece no WhatsApp:
            </p>
            
            {/* Container com background do WhatsApp */}
            <div 
              className="relative p-4 rounded-lg overflow-hidden"
              style={{
                backgroundImage: 'url(/background-whatsapp.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                minHeight: '300px'
              }}
            >
              {/* Overlay sutil para melhorar legibilidade */}
              <div className="absolute inset-0 bg-black bg-opacity-5"></div>
              
              {/* Chat Bubble - Perspectiva do Receptor */}
              <div className="relative flex justify-start">
                <div className="max-w-xs">
                {/* Avatar do Bot */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <span className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Bot
                  </span>
                </div>

                {/* Mensagem */}
                <div className="bg-white rounded-2xl rounded-tl-sm shadow-sm border border-gray-200">
                  {/* Header com mídia */}
                  {template.components
                    .filter(comp => comp.type === 'HEADER')
                    .map((comp, idx) => (
                      <div key={idx}>
                        {renderHeaderMedia(comp)}
                        {comp.format === 'TEXT' && comp.text && (
                          <div className="px-4 pt-3 pb-2">
                            <p className="text-sm font-medium text-gray-900">
                              {processTextWithVariables(comp.text)}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}

                  {/* Corpo da mensagem */}
                  {template.components
                    .filter(comp => comp.type === 'BODY')
                    .map((comp, idx) => (
                      <div key={idx} className="px-4 py-2">
                        <p className="text-sm text-gray-900 leading-relaxed">
                          {processTextWithVariables(comp.text || '')}
                        </p>
                      </div>
                    ))}

                  {/* Footer */}
                  {template.components
                    .filter(comp => comp.type === 'FOOTER')
                    .map((comp, idx) => (
                      <div key={idx} className="px-4 pb-2">
                        <p className="text-xs text-gray-500">
                          {processTextWithVariables(comp.text || '')}
                        </p>
                      </div>
                    ))}

                  {/* Botões */}
                  {template.components
                    .filter(comp => comp.type === 'BUTTONS')
                    .map((comp, idx) => (
                      <div key={idx}>
                        {renderButtons(comp)}
                      </div>
                    ))}

                  {/* Timestamp */}
                  <div className="px-4 pb-2 flex justify-end">
                    <span className="text-xs text-gray-500">{currentTime}</span>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
